import { Topics } from '@common/interfaces/topics';
import { persistState, selectPersistStateInit } from '@datorama/akita';
import { LastUpdateState } from '@ep-om/project/state/lastUpdate/lastUpdate.store';
import * as localForage from 'localforage';
import { debounceTime, take, toArray } from 'rxjs/operators';
import { akitaPersistStorage, isPersistedSnapshotLoaded } from './akita-persist-storage';
import { databaseVersionReady, DB_NAME, DB_STORE_NAME, DB_VERSION } from './indexeddb-version';
import { persistRegistry, registerPersistedStore } from './persist-registry';
import {
  AUTH_SNAPSHOT_STORAGE_KEY,
  canRuntimePersist,
  getRuntimeCacheEpoch,
  initializeRuntimeCacheEpoch,
} from './session-cache';

const debounceTimeMs = 100;

// Solo gli store con configurazione propria (hook/storage/debounce dedicati) vivono qui, in
// CONFIGURED_STORES. Tutti gli altri store persistiti si auto-registrano col decoratore
// @PersistedStoreConfig (vedi persisted-store-config.ts): questo file non elenca piu' gli store
// standard.

// Oltre questa soglia si rinuncia ad attendere l'idratazione e si avvia comunque l'app: meglio
// partire con gli store vuoti (li ripopola il realtime) che restare su una pagina bianca se IDB
// non risponde - tipicamente un upgrade di versione bloccato da un'altra tab.
const HYDRATION_GATE_TIMEOUT_MS = 15000;

localForage.config({
  driver: localForage.INDEXEDDB,
  name: DB_NAME,
  storeName: DB_STORE_NAME,
  version: DB_VERSION
});

type PersistedStoreConfig = {
  /** Chiave su storage. Coincide col nome dello store, salvo i casi in cui `include` e' esplicito. */
  key: string;
  /** Default: [key]. */
  include?: string[];
  /** Default: lo storage con snapshot unico su IDB. */
  storage?: any;
  /** Default: true (scrittura debounced di debounceTimeMs). */
  debounce?: boolean;
  preStorageUpdate?: (storeName: string, state: any) => any;
  preStoreUpdate?: (storeName: string, state: any) => any;
};

/**
 * Anche auth e' protetto dal fencing dell'epoch: una vecchia tab con un refresh HTTP gia' in volo
 * non deve poter sovrascrivere la nuova sessione dopo un'impersonificazione. La scrittura di commit
 * della nuova auth bypassa intenzionalmente questo adapter e usa localStorage direttamente.
 */
const authPersistStorage = {
  async getItem(key: string): Promise<string | null> {
    await databaseVersionReady;
    await initializeRuntimeCacheEpoch();
    return localStorage.getItem(key);
  },
  setItem(key: string, value: any): any {
    if (canRuntimePersist()) {
      localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
    }
    return value;
  },
  removeItem(key: string): void {
    if (canRuntimePersist()) {
      localStorage.removeItem(key);
    }
  },
  clear(): void {
    if (canRuntimePersist()) {
      localStorage.clear();
    }
  },
};

/** Lo store `active` e' effimero: non ha senso ripristinarlo dalla cache al riavvio. */
const stripActive = (_storeName: string, state: any) => {
  const { active, ...rest } = state;
  return rest;
};

// Store con configurazione propria.
const CONFIGURED_STORES: PersistedStoreConfig[] = [
  {
    key: 'lastUpdate',
    // Lo `status` e' transitorio: su storage vanno solo i cursori.
    preStorageUpdate(_storeName, state: LastUpdateState) {
      return Object.entries(state || {}).reduce((newState, [key, val]) => {
        if (!val) {
          newState[key] = val;
        } else {
          const { status, ...rest } = val;
          newState[key] = rest;
        }
        return newState;
      }, {});
    },
  },
  { key: Topics.PROJECTS, preStorageUpdate: stripActive },
  { key: Topics.ISSUES, preStorageUpdate: stripActive },
  // Stato piccolo, si vuole la scrittura immediata: nessun debounce.
  { key: 'action', debounce: false },
  { key: 'ui', debounce: false },
  // Unico store su localStorage: createInitialAuthState() legge il token in modo sincrono
  // all'avvio, quindi non puo' dipendere da IDB.
  {
    key: AUTH_SNAPSHOT_STORAGE_KEY,
    include: ['auth'],
    storage: authPersistStorage,
    debounce: false,
    preStoreUpdate: (_storeName, state) => ({
      ...state,
      cacheEpoch: state.cacheEpoch || getRuntimeCacheEpoch(),
    }),
  },
];

// Anche i configurati risultano "persistiti" per il registry (persisted-store-registry.service).
// PROJECTS e ISSUES non compaiono: si auto-registrano col decoratore (persistence: 'external',
// che li riporta comunque a questi persistState con hook custom).
['lastUpdate', 'action', 'ui', 'auth'].forEach((name) => registerPersistedStore({ name, persistence: 'external' }));

const createPersistStorage = (store: PersistedStoreConfig) => persistState({
  key: store.key,
  include: store.include || [store.key],
  storage: store.storage || akitaPersistStorage,
  // Gli hook vanno passati solo se presenti: persistState fa Object.assign sui default, quindi una
  // chiave a undefined sovrascriverebbe l'implementazione di default con undefined.
  ...(store.debounce === false ? {} : { preStorageUpdateOperator: () => debounceTime(debounceTimeMs) }),
  ...(store.preStorageUpdate ? { preStorageUpdate: store.preStorageUpdate } : {}),
  ...(store.preStoreUpdate ? { preStoreUpdate: store.preStoreUpdate } : {}),
});

/**
 * Crea tutte le persistState (configurate + registrate col decoratore) e restituisce la promise su
 * cui va atteso il bootstrap (vedi main.ts): si risolve quando OGNI persistState ha completato
 * l'init, cioe' quando tutte le sottoscrizioni a $$addStore sono attive. Finche' non e' risolta
 * nessuno store Akita deve essere creato.
 *
 * E' la correzione strutturale del bug per cui l'app partiva senza dati pur avendoli su IDB:
 * persistState sottoscrive $$addStore solo dopo che la sua getItem si e' risolta, e $$addStore e'
 * un ReplaySubject(50, 5000). Uno store creato piu' di 5 secondi (o piu' di 50 store) prima che la
 * sua getItem tornasse non veniva idratato mai piu', per quanto si attendesse. Attendendo qui, ogni
 * store viene invece idratato in modo sincrono dentro il proprio costruttore: quando la sua
 * creazione emette su $$addStore la sottoscrizione c'e' gia' e setInitial() gira subito.
 *
 * Va chiamata dal body di main.ts: a quel punto l'intero grafo eager dei moduli e' gia' stato
 * valutato, quindi ogni @PersistedStoreConfig eager si e' gia' registrato ed e' contato qui.
 * Le registrazioni successive (chunk lazy, store runtime) creano la persistState al volo sul
 * percorso sincrono dello snapshot (vedi persist-registry.ts) e non vengono contate.
 */
export function startPersistEngine(): Promise<void> {
  const expected = CONFIGURED_STORES.length + persistRegistry.pendingKeyCount();

  // Va sottoscritto PRIMA di creare le istanze: _persistStateInit e' un ReplaySubject(1), quindi un
  // sottoscrittore tardivo vedrebbe solo l'ultima emissione e il conteggio sarebbe falsato.
  const allPersistStatesInitialized = selectPersistStateInit()
    .pipe(take(expected), toArray())
    .toPromise();

  CONFIGURED_STORES.forEach(createPersistStorage);
  persistRegistry.start((key) => createPersistStorage({ key }), isPersistedSnapshotLoaded);

  return Promise.race([
    allPersistStatesInitialized.then(() => 'ready' as const),
    new Promise<'timeout'>((resolve) => setTimeout(() => resolve('timeout'), HYDRATION_GATE_TIMEOUT_MS)),
  ]).then((outcome) => {
    if (outcome === 'timeout') {
      console.error(`[LOCALFORAGE] idratazione non completata entro ${HYDRATION_GATE_TIMEOUT_MS}ms: avvio comunque`);
      return;
    }
    console.log(`[LOCALFORAGE] ${expected} store persistiti pronti per l'idratazione`);
  });
}
